<template>
  <div>
    <v-card
      border
      class="rounded-lg"
    >
      <div
        style="height: 170px"
        class="blue-grey lighten-5 pa-0 position-relative material-card-image"
      >
        <div class="material-card__preview-actions">
          <div class="material-card__preview-actions-container">
            <button
              class="material-card__preview-actions-button"
              @click.stop="handlePreviewClick"
            >
              <v-icon
                :icon="isVideoMaterial ? 'mdi-play' : 'mdi-magnify-plus-outline'"
                color="primary"
                size="24"
              />
            </button>
            <button
              v-if="canEdit"
              class="material-card__preview-actions-button"
              @click.stop="$emit('edit')"
            >
              <v-icon
                icon="mdi-pencil"
                color="primary"
                size="24"
              />
            </button>
            <button
              v-if="canDelete"
              class="material-card__preview-actions-button"
              @click.stop="$emit('delete')"
            >
              <v-icon
                icon="mdi-delete"
                color="error"
                size="24"
              />
            </button>
          </div>
        </div>
        <video
          v-if="isVideoMaterial && url && !showIcon"
          ref="videoElement"
          :src="url"
          class="material-card__video"
          muted
          playsinline
          @loadedmetadata="handleVideoLoaded"
          @error="handleImageError"
        />
        <r-img
          v-else-if="optimizedThumbnailUrl && !showIcon"
          :src="optimizedThumbnailUrl"
          class="bg-grey-lighten-3"
          height="170px"
          @error="handleImageError"
        />
        <div
          v-else-if="icon"
          class="pa-10"
        >
          <r-img
            :src="icon"
            height="100px"
          />
        </div>
        <div
          v-else
          class="material-card__fallback"
        />
        <v-chip
          v-if="isNew"
          color="primary"
          size="small"
          variant="elevated"
          rounded
          class="position-absolute top-0 right-0 ma-2"
        >
          {{ $t('labels.new') }}
        </v-chip>
      </div>

      <v-divider />
      <v-tooltip
        location="top"
        v-if="caption"
      >
        <template v-slot:activator="{ props }">
          <span
            v-bind="props"
            class="text-truncate w-100 text-caption px-2"
            v-text="caption"
          />
        </template>
        <span>{{ caption }}</span>
      </v-tooltip>

      <v-card-actions
        class="white py-1 material-card__actions"
        :style="{ 'margin-top': caption ? '-18px' : undefined }"
      >
        <v-tooltip location="top">
          <template v-slot:activator="{ props }">
            <span
              v-bind="props"
              class="flex-grow-1 text-truncate"
              v-text="name"
            />
          </template>
          <span>{{ name }}</span>
        </v-tooltip>

        <v-tooltip location="top">
          {{ $t('tooltips.create-marketing-content') }}
          <template v-slot:activator="{ props }">
            <v-btn
              v-bind="props"
              v-if="isPublishable && topicId && imageId && imageSource"
              icon
              :to="{
                name: 'content-creator',
                query: { topicId, imageId, imageSource, section: 'creator' },
              }"
            >
              <v-icon>mdi-brain</v-icon>
            </v-btn>
          </template>
        </v-tooltip>
        <v-tooltip location="top">
          {{ $t('tooltips.download') }}
          <template v-slot:activator="{ props }">
            <v-btn
              v-bind="props"
              icon
              :href="url"
              @click="$emit('download')"
            >
              <v-icon>mdi-download</v-icon>
            </v-btn>
          </template>
        </v-tooltip>
      </v-card-actions>
    </v-card>

    <v-dialog
      v-model="showPreviewDialog"
      width="min(1200px, 96vw)"
      scrollable
    >
      <v-card>
        <v-card-title class="material-card-preview__header">
          <span
            class="text-truncate"
            v-text="name"
          />
          <v-btn
            icon="mdi-close"
            variant="text"
            @click="showPreviewDialog = false"
          />
        </v-card-title>
        <v-card-text class="material-card-preview__body">
          <video
            v-if="isVideoMaterial"
            :src="url"
            controls
            class="material-card-preview__video"
          />
          <div
            v-else-if="isPdf"
            class="material-card-preview__pdf"
          >
            <VuePdfEmbed
              v-if="showPreviewDialog"
              :source="previewUrl"
            />
          </div>
          <r-img
            v-else-if="previewImageUrl"
            :src="previewImageUrl"
          />
          <div
            v-else-if="icon"
            class="d-flex justify-center pa-10"
          >
            <r-img
              :src="icon"
              height="200px"
            />
          </div>
        </v-card-text>
        <v-card-actions>
          <v-btn @click="showPreviewDialog = false">
            {{ $t('labels.close') }}
          </v-btn>
          <v-spacer />
          <v-btn
            :href="url"
            @click="$emit('download')"
          >
            <v-icon icon="mdi-download" />
            {{ $t('tooltips.download') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { defineAsyncComponent } from 'vue'

import { setQueryParams } from '@/lib/url'
import { isPdfFile, isVideoFile } from '@/modules/adMaterials/fileTypes'

const VuePdfEmbed = defineAsyncComponent(() => import('vue-pdf-embed'))

export default {
  components: { VuePdfEmbed },
  emits: ['preview', 'download', 'edit', 'delete'],
  props: {
    caption: { type: String, default: '' },
    name: { type: String, default: '' },
    url: { type: String, required: true },
    icon: { type: String, default: '' },
    thumbnailUrl: { type: String, default: '' },
    topicId: { type: String, default: '' },
    imageId: { type: String, default: '' },
    isNew: { type: Boolean, default: false },
    imageSource: { type: String, default: '' },
    canEdit: { type: Boolean, default: false },
    canDelete: { type: Boolean, default: false },
    isImage: { type: Boolean, default: false },
    isVideo: { type: Boolean, default: false },
    openPreviewFallback: { type: Boolean, default: true },
  },

  data() {
    return {
      showPreviewDialog: false,
      showIcon: false,
    }
  },

  computed: {
    isPdf() {
      return this.url && isPdfFile(this.url)
    },
    optimizedThumbnailUrl() {
      if (this.thumbnailUrl && !isPdfFile(this.thumbnailUrl)) {
        return this.setQueryParams(this.thumbnailUrl, { h: 340 })
      }
      return null
    },
    isVideoMaterial() {
      return this.isVideo || (this.url && isVideoFile(this.url))
    },
    isPublishable() {
      return this.isImage || this.isVideoMaterial
    },
    previewUrl() {
      if (!this.isPdf) {
        return this.url
      }

      try {
        return this.setQueryParams(this.url, { attachment: false })
      } catch (error) {
        return this.url
      }
    },
    previewImageUrl() {
      return !this.isPdf && !this.isVideoMaterial ? this.url : ''
    },
  },
  methods: {
    setQueryParams,
    handlePreviewClick() {
      this.$emit('preview')

      if (!this.openPreviewFallback || !this.url || typeof window === 'undefined') {
        return
      }

      this.showPreviewDialog = true
    },
    handleImageError() {
      this.showIcon = true
    },
    handleVideoLoaded() {
      const video = this.$refs.videoElement
      if (video && video.duration >= 1) {
        video.currentTime = 1.0
      } else if (video && video.duration > 0) {
        video.currentTime = 0.1
      }
    },
  },
}
</script>
<style scoped>
.v-card-actions {
  gap: unset;
}
.material-card__actions .v-btn {
  padding-left: 0;
  padding-right: 0;
  width: calc(var(--v-btn-height));
}
.text-caption {
  font-style: italic;
}

.material-card__preview-actions {
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.2);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1;
  opacity: 0;
  transition: opacity 0.2s ease-in-out;
}

.material-card__preview-actions-container {
  display: flex;
  gap: 12px;
  align-items: center;
}

.material-card__preview-actions-button {
  background: white !important;
  border-radius: 100%;
  padding: 8px;
  border: none;
  cursor: pointer;
  transition: transform 0.2s ease-in-out;
}

.material-card__preview-actions-button:hover {
  transform: scale(1.1);
}

.material-card__preview-actions-button:focus-visible {
  outline: none;
}

.material-card-image:hover .material-card__preview-actions,
.material-card-image:focus-within .material-card__preview-actions {
  opacity: 1;
}

.material-card__video {
  width: 100%;
  height: 170px;
  object-fit: cover;
  background-color: rgb(var(--v-theme-grey-lighten-3));
}

.material-card__fallback {
  height: 170px;
  background-color: rgb(var(--v-theme-grey-lighten-3));
}

.material-card-preview__header {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 12px;
}

.material-card-preview__body {
  max-height: 82vh;
  overflow: auto;
}

.material-card-preview__pdf {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.material-card-preview__video {
  width: 100%;
  max-height: 78vh;
  background-color: #000;
}
</style>
